// ============================================
// INITIALIZATION FUNCTION
// ============================================

/**
 * Initialize the Bio Orthotics Assessment
 * @param {Object} config - Configuration object with screens, decisionRules, and productData
 */
function initBioOrthoticsAssessment(config) {
    // Validate config
    if (!config) {
        console.error('Bio Orthotics: No configuration provided');
        return;
    }

    const screens = config.screens || [];
    const decisionRules = config.decisionRules || [];
    const productData = config.productData || {};

    // Validate required data
    if (screens.length === 0) {
        console.error('Bio Orthotics: No screens provided in configuration');
        return;
    }
    
    if (decisionRules.length === 0) {
        console.error('Bio Orthotics: No decision rules provided in configuration');
        return;
    }
    
    if (Object.keys(productData).length === 0) {
        console.error('Bio Orthotics: No product data provided in configuration');
        return;
    }

    // Initialize the assessment controller with configuration
    document.addEventListener('DOMContentLoaded', () => {
        new AssessmentController(screens, decisionRules, productData);
    });
}

// ============================================
// DECISION ENGINE
// ============================================

class DecisionEngine {
    constructor(rules) {
        this.rules = rules;
        this.answers = {};
    }

    setAnswer(questionId, value) {
        this.answers[questionId] = value;
    }

    getRecommendation() {
        // Get answers using normalized keys
        const size = this.answers['size'] || 'm';
        const footwear = this.answers['footwear-type'] || 'other';
        const shank = this.answers['shoe-shank'] || 'yes';
        const archHeight = this.answers['arch-height'] || null;
        const mobility = this.answers['foot-mobility'] || 'normal';
        const supination = this.answers['supination-resistance'] || 'moderate';
        const calcaneal = this.answers['calcaneal-stance'] || 'neutral';
        const limbLength = this.answers['limb-length'] || 'no';
        const heelPain = this.answers['heel-pain'] || 'no';

        // Find matching rule
        const matchingRule = this.rules.find(rule => {
            const c = rule.conditions;
            
            // Check mobility first (poor mobility short-circuits other checks)
            if (mobility === 'poor' && c.mobility === 'poor') {
                // For poor mobility, just need shank to match
                if (c.shank !== undefined && c.shank !== shank) return false;
                return true;
            }
            
            // For normal mobility, check all conditions
            if (c.mobility === 'normal' && mobility === 'normal') {
                const footwearMatch = c.footwear === footwear;
                const shankMatch = c.shank === shank;
                const supinationMatch = c.supination === supination;
                const calcanealMatch = c.calcaneal === calcaneal;
                
                // Arch height only matters when shank is 'no'
                let archMatch = true;
                if (shank === 'no' && c.archHeight) {
                    archMatch = c.archHeight === archHeight;
                }
                
                return footwearMatch && shankMatch && archMatch && supinationMatch && calcanealMatch;
            }
            
            return false;
        });

        // Build component suggestions
        const components = [];
        if (limbLength === 'left') {
            components.push('You could add a Rearfoot Heel Lift to the left insole (5mm lift included in component pack).');
        } else if (limbLength === 'right') {
            components.push('You could add a Rearfoot Heel Lift to the right insole (5mm lift included in component pack).');
        }
        if (heelPain === 'left') {
            components.push('You could add a Heel Cushion to the left insole (3mm cushion included in component pack).');
        } else if (heelPain === 'right') {
            components.push('You could add a Heel Cushion to the right insole (3mm cushion included in component pack).');
        } else if (heelPain === 'both') {
            components.push('You could add Heel Cushions to both insoles (3mm cushions included in component pack).');
        }

        if (matchingRule) {
            const rec = matchingRule.recommendation;
            const alt = matchingRule.alternative || null;
            const result = {
                device: rec.device,
                density: rec.density || null,
                posting: rec.posting || null,
                postingDisplay: rec.postingDisplay || null,
                narrative: rec.narrative,
                productKey: rec.productKey,
                imageKey: rec.imageKey,
                size: size,
                components: components,
                alternative: null
            };
            if (alt) {
                result.alternative = {
                    device: alt.device,
                    posting: alt.posting || null,
                    postingDisplay: alt.postingDisplay || null,
                    narrative: alt.narrative,
                    productKey: alt.productKey,
                    imageKey: alt.imageKey
                };
            }
            return result;
        }

        // Fallback if no rule matches
        return {
            device: 'Bio Advanced',
            density: 'Medium',
            posting: null,
            postingDisplay: null,
            narrative: 'Please consult with a specialist for a personalized recommendation.',
            productKey: 'bio_advance_full_length_medium',
            imageKey: 'bio_advance_full_length_medium_none',
            size: size,
            components: components,
            alternative: null
        };
    }

    getAnswersSummary() {
        return this.answers;
    }
}

// ============================================
// ASSESSMENT CONTROLLER
// ============================================

class AssessmentController {
    constructor(screens, decisionRules, productData) {
        // Store configuration
        this.screens = screens;
        this.productData = productData;
        
        // Initialize state
        this.currentStep = 0;
        this.totalSteps = screens.length;
        this.decisionEngine = new DecisionEngine(decisionRules);
        
        // Cache screen indices for skip logic
        this.screenIndices = {
            shellPreference: this.screens.findIndex(s => s.questions.some(q => q.id === 'shell-preference')),
            shoeShank: this.screens.findIndex(s => s.questions.some(q => q.id === 'shoe-shank')),
            mobility: this.screens.findIndex(s => s.questions.some(q => q.id === 'foot-mobility')),
            archHeight: this.screens.findIndex(s => s.questions.some(q => q.id === 'arch-height')),
            supination: this.screens.findIndex(s => s.questions.some(q => q.id === 'supination-resistance')),
            calcaneal: this.screens.findIndex(s => s.questions.some(q => q.id === 'calcaneal-stance')),
            limbLength: this.screens.findIndex(s => s.questions.some(q => q.id === 'limb-length')),
            heelPain: this.screens.findIndex(s => s.questions.some(q => q.id === 'heel-pain'))
        };
        
        // Get DOM elements
        this.questionContainer = document.getElementById('assmt-question-container');
        this.questionContainerMain = document.getElementById('assmt-question-container-main');
        this.questionContainerOverlay = document.getElementById('assmt-question-container-overlay');
        this.questionContainerOverlayContent = document.getElementById('assmt-question-container-overlay-content');
        this.questionWrapper = document.querySelector('.assmt__question-wrapper');
        this.prevBtn = document.getElementById('assmt-prev-btn');
        this.nextBtn = document.getElementById('assmt-next-btn');
        this.progressDots = document.getElementById('assmt-progress-dots');
        this.questionNavigation = this.prevBtn.parentElement;
        this.resultsContainer = document.getElementById('assmt-results-container');
        this.assessmentContainer = document.querySelector('.assmt__container');
        
        this.videoModal = document.getElementById('assmt-video-modal');
        this.modalClose = document.getElementById('assmt-modal-close');
        this.modalVideo = document.getElementById('assmt-modal-video');
        this.modalIframe = document.getElementById('assmt-modal-iframe');
        this.modalTitle = document.getElementById('assmt-modal-title');
        
        this.printBtn = document.getElementById('assmt-print-btn');
        
        this.heroSection = document.querySelector('.assmt__hero');

        const assmtEl = document.querySelector('.assmt');
        this.logoSrc = assmtEl ? assmtEl.dataset.printLogo : '';
        
        this.isTransitioning = false;
        
        this.init();
    }

    init() {
        this.renderScreen();
        this.renderProgressDots();
        this.setupEventListeners();
    }

    setupEventListeners() {
        this.prevBtn.addEventListener('click', () => this.previousQuestion());
        this.nextBtn.addEventListener('click', () => this.nextQuestion());

        // Video modal listeners
        this.modalClose.addEventListener('click', () => this.closeVideoModal());
        this.videoModal.addEventListener('click', (e) => {
            if (e.target === this.videoModal) {
                this.closeVideoModal();
            }
        });
        
        // ESC key to close modal
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.videoModal.classList.contains('assmt-modal--visible')) {
                this.closeVideoModal();
            }
        });
        
        // Print button
        if (this.printBtn) {
            this.printBtn.addEventListener('click', () => this.printOutcome('all'));
        }
    }

    printOutcome(which) {
        const resultsContent = document.getElementById('assmt-results-product');
        if (!resultsContent) return;
        
        resultsContent.classList.remove('assmt-results--print-1', 'assmt-results--print-2');
        
        if (which === '1') {
            resultsContent.classList.add('assmt-results--print-1');
        } else if (which === '2') {
            resultsContent.classList.add('assmt-results--print-2');
        }
        
        window.print();
        
        setTimeout(() => {
            resultsContent.classList.remove('assmt-results--print-1', 'assmt-results--print-2');
        }, 500);
    }

    toggleHero(show) {
        if (!this.heroSection) return;
        
        if (show) {
            this.heroSection.style.display = 'block';
            this.heroSection.style.opacity = '0';
            this.heroSection.style.maxHeight = '0';
            this.heroSection.style.marginBottom = '0';
            this.heroSection.style.paddingTop = '0';
            this.heroSection.style.paddingBottom = '0';
            this.heroSection.style.transition = 'max-height 0.5s ease-out, opacity 0.5s ease-out, margin-bottom 0.5s ease-out, padding 0.5s ease-out';
            
            void this.heroSection.offsetHeight;
            
            requestAnimationFrame(() => {
                this.heroSection.style.maxHeight = '500px';
                this.heroSection.style.opacity = '1';
                this.heroSection.style.marginBottom = '30px';
                this.heroSection.style.paddingTop = '30px';
                this.heroSection.style.paddingBottom = '30px';
            });
            
            setTimeout(() => {
                this.heroSection.style.maxHeight = '';
                this.heroSection.style.transition = '';
            }, 500);
        } else {
            const currentHeight = this.heroSection.offsetHeight;
            this.heroSection.style.maxHeight = currentHeight + 'px';
            this.heroSection.style.transition = 'max-height 0.5s ease-in, opacity 0.5s ease-in, margin-bottom 0.5s ease-in, padding 0.5s ease-in';
            
            void this.heroSection.offsetHeight;
            
            requestAnimationFrame(() => {
                this.heroSection.style.maxHeight = '0';
                this.heroSection.style.opacity = '0';
                this.heroSection.style.marginBottom = '0';
                this.heroSection.style.paddingTop = '0';
                this.heroSection.style.paddingBottom = '0';
            });
            
            setTimeout(() => {
                this.heroSection.style.display = 'none';
                this.heroSection.style.maxHeight = '';
                this.heroSection.style.opacity = '';
                this.heroSection.style.marginBottom = '';
                this.heroSection.style.paddingTop = '';
                this.heroSection.style.paddingBottom = '';
                this.heroSection.style.transition = '';
            }, 500);
        }
    }

    // Helper: check if a screen should be skipped
    shouldSkipScreen(screenIndex) {
        const shellPref = this.decisionEngine.answers['shell-preference'];
        
        // Shell preference: soft → skip shank + arch height
        if (shellPref === 'soft') {
            if (screenIndex === this.screenIndices.shoeShank) return true;
            if (screenIndex === this.screenIndices.archHeight) return true;
        }
        
        // Shell preference: rigid → skip shank only (arch height shows)
        if (shellPref === 'rigid') {
            if (screenIndex === this.screenIndices.shoeShank) return true;
        }
        
        // Shell preference: no → normal flow, arch height still conditional on shank
        if (screenIndex === this.screenIndices.archHeight) {
            if (!shellPref || shellPref === 'no') {
                return this.decisionEngine.answers['shoe-shank'] !== 'no';
            }
        }
        
        // Poor mobility skips supination + calcaneal
        const isPoorMobility = this.decisionEngine.answers['foot-mobility'] === 'poor';
        if (!isPoorMobility) return false;
        return screenIndex === this.screenIndices.supination || screenIndex === this.screenIndices.calcaneal;
    }

    // Get next valid screen index (skipping screens that should be skipped)
    getNextScreenIndex(fromIndex) {
        let next = fromIndex + 1;
        while (next < this.totalSteps && this.shouldSkipScreen(next)) {
            next++;
        }
        return next;
    }

    // Get previous valid screen index (skipping screens that should be skipped)
    getPrevScreenIndex(fromIndex) {
        let prev = fromIndex - 1;
        while (prev >= 0 && this.shouldSkipScreen(prev)) {
            prev--;
        }
        return prev;
    }

    // Helper method to generate HTML for a single question
    generateQuestionHTML(question, savedAnswer = null) {
        // Handle size-selector type differently
        if (question.type === 'size-selector') {
            return this.generateSizeSelectorHTML(question, savedAnswer);
        }
        
        return `
            <div class="assmt-question__header">
                ${question._autoNumber ? `<div class="assmt-question__number">${question._autoNumber}.</div>` : ''}
                <h2 class="assmt-question__title">${question.title}</h2>
            </div>
            <p class="assmt-question__description">${question.description}</p>
            <div class="assmt-question__options ${question.options[0].image ? 'assmt-question__options--with-images' : ''}">
                ${question.options.map(option => `
                    <div class="assmt-option ${option.image ? 'assmt-option--with-image' : ''}">
                        ${option.image ? `<img src="${option.image}" alt="${option.label}" class="assmt-option__image" loading="eager" width="238" height="238">` : ''}
                        <button 
                            class="assmt-option__button ${savedAnswer === option.value ? 'assmt-option__button--selected' : ''}" 
                            data-question="${question.id}" 
                            data-value="${option.value}"
                        >
                            ${option.label}
                        </button>
                    </div>
                `).join('')}
            </div>
            ${question.videoLink ? `
                <div class="assmt-question__video">
                    <div class="assmt-question__video-title">${question.videoText || 'How to perform patient test?'}</div>
                    <a href="${question.videoLink}" class="assmt-question__video-link" data-video-link="${question.videoLink}">
                        <span class="assmt-question__video-text">Play demonstration video</span>
                        <span class="assmt-question__video-icon"></span>
                    </a>
                </div>
            ` : ''}
        `;
    }
    
    // Generate HTML for size selector with region and size dropdowns
    generateSizeSelectorHTML(question, savedAnswer = null) {
        const sizeChart = question.sizeChart;
        const regions = Object.keys(sizeChart);
        const defaultRegion = regions[0];
        
        const savedRegion = this.decisionEngine.answers['size-region'] || defaultRegion;
        const sizes = sizeChart[savedRegion].sizes;
        
        return `
            ${question.description ? `<p class="assmt-size-selector__description">${question.description}</p>` : ''}
            <div class="assmt-size-selector" data-question="${question.id}">
                <div class="assmt-size-selector__row">
                    <label class="assmt-size-selector__label">Size:</label>
                    <div class="assmt-size-selector__dropdowns">
                        <select class="assmt-size-selector__select assmt-size-selector__region" data-size-chart='${JSON.stringify(sizeChart)}'>
                            ${regions.map(region => `
                                <option value="${region}" ${savedRegion === region ? 'selected' : ''}>
                                    ${sizeChart[region].label}
                                </option>
                            `).join('')}
                        </select>
                        <select class="assmt-size-selector__select assmt-size-selector__size" data-question="${question.id}">
                            <option value="">Select size</option>
                            ${Object.entries(sizes).map(([value, label]) => `
                                <option value="${value}" ${savedAnswer === value ? 'selected' : ''}>
                                    ${label}
                                </option>
                            `).join('')}
                        </select>
                    </div>
                </div>
            </div>
        `;
    }

    // Helper method to generate HTML for a screen (one or more questions)
    generateScreenHTML(screen) {
        const questions = screen.questions;
        
        return questions.map(question => {
            const savedAnswer = this.decisionEngine.answers[question.id];
            if (question.type !== 'size-selector') {
                question._autoNumber = this.getVisibleQuestionNumber(question.id);
            }
            
            const isConditional = question.conditionalOn;
            let isHidden = false;
            
            if (isConditional) {
                const parentAnswer = this.decisionEngine.answers[isConditional.questionId];
                isHidden = parentAnswer !== isConditional.value;
            }
            
            return `
                <div class="assmt-question ${questions.length > 1 ? 'assmt-question--multi' : ''}" 
                     data-question-id="${question.id}"
                     ${isConditional ? `data-conditional-on="${isConditional.questionId}" data-conditional-value="${isConditional.value}"` : ''}
                     style="${isHidden ? 'display: none; opacity: 0;' : ''}">
                    ${this.generateQuestionHTML(question, savedAnswer)}
                </div>
            `;
        }).join('');
    }

    renderScreen() {
        const screen = this.screens[this.currentStep];
        
        this.questionContainer.innerHTML = this.generateScreenHTML(screen);

        // Add click handlers to options
        const optionButtons = this.questionContainer.querySelectorAll('.assmt-option__button');
        optionButtons.forEach(btn => {
            btn.addEventListener('click', (e) => this.handleOptionClick(e));
        });

        // Add click handlers to video links
        const videoLinks = this.questionContainer.querySelectorAll('.assmt-question__video-link');
        videoLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const videoLink = link.dataset.videoLink;
                this.openVideoModal({ videoLink });
            });
        });
        
        // Add change handlers for size selector dropdowns
        const regionSelects = this.questionContainer.querySelectorAll('.assmt-size-selector__region');
        regionSelects.forEach(select => {
            select.addEventListener('change', (e) => this.handleRegionChange(e));
        });
        
        const sizeSelects = this.questionContainer.querySelectorAll('.assmt-size-selector__size');
        sizeSelects.forEach(select => {
            select.addEventListener('change', (e) => this.handleSizeChange(e));
        });

        this.updateNavigationButtons();
    }
    
    handleRegionChange(e) {
        const regionSelect = e.currentTarget;
        const sizeChart = JSON.parse(regionSelect.dataset.sizeChart);
        const selectedRegion = regionSelect.value;
        const sizes = sizeChart[selectedRegion].sizes;
        
        this.decisionEngine.setAnswer('size-region', selectedRegion);
        const selectedOption = regionSelect.options[regionSelect.selectedIndex];
        this.decisionEngine.setAnswer('size-region-label', selectedOption.text);
        
        const container = regionSelect.closest('.assmt-size-selector');
        const sizeSelect = container.querySelector('.assmt-size-selector__size');
        const currentSizeValue = sizeSelect.value;

        if (sizeSelect.value) {
            const sizeOption = sizeSelect.options[sizeSelect.selectedIndex];
            this.decisionEngine.setAnswer('size-label', sizeOption.text);
        }
        
        sizeSelect.innerHTML = `
            <option value="">Select size</option>
            ${Object.entries(sizes).map(([value, label]) => `
                <option value="${value}" ${currentSizeValue === value ? 'selected' : ''}>
                    ${label}
                </option>
            `).join('')}
        `;
        
        this.updateNavigationButtons();
    }
    
    handleSizeChange(e) {
        const sizeSelect = e.currentTarget;
        const questionId = sizeSelect.dataset.question;
        const value = sizeSelect.value;
        
        if (value) {
            this.decisionEngine.setAnswer(questionId, value);
            const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
            this.decisionEngine.setAnswer('size-label', selectedOption.text);
        } else {
            delete this.decisionEngine.answers[questionId];
            delete this.decisionEngine.answers['size-label'];
        }
        
        this.updateNavigationButtons();
    }

    handleOptionClick(e) {
        const button = e.currentTarget;
        const questionId = button.dataset.question;
        const value = button.dataset.value;

        // Remove selected class from all options in THIS question only
        const questionContainer = button.closest('.assmt-question');
        const questionOptions = questionContainer.querySelectorAll('.assmt-option__button');
        questionOptions.forEach(opt => opt.classList.remove('assmt-option__button--selected'));

        // Add selected class to clicked option
        button.classList.add('assmt-option__button--selected');

        // Save answer
        this.decisionEngine.setAnswer(questionId, value);

        // Show/hide conditional questions based on this answer
        const allConditionals = this.questionContainer.querySelectorAll('.assmt-question[data-conditional-on]');
        allConditionals.forEach(condQuestion => {
            const parentId = condQuestion.dataset.conditionalOn;
            const requiredValue = condQuestion.dataset.conditionalValue;
            const parentAnswer = this.decisionEngine.answers[parentId];
            
            if (parentId === questionId) {
                if (parentAnswer === requiredValue) {
                    condQuestion.style.display = '';
                    requestAnimationFrame(() => {
                        condQuestion.style.transition = 'opacity 0.3s ease';
                        condQuestion.style.opacity = '1';
                    });
                } else {
                    condQuestion.style.transition = 'opacity 0.3s ease';
                    condQuestion.style.opacity = '0';
                    setTimeout(() => {
                        condQuestion.style.display = 'none';
                    }, 300);
                    // Clear the conditional answer when hidden
                    const condId = condQuestion.dataset.questionId;
                    delete this.decisionEngine.answers[condId];
                }
            }
        });

        // Shell preference sets implicit shank answer
        if (questionId === 'shell-preference') {
            if (value === 'soft') {
                this.decisionEngine.setAnswer('shoe-shank', 'yes');
                // Clear arch height since it's skipped
                delete this.decisionEngine.answers['arch-height'];
            } else if (value === 'rigid') {
                this.decisionEngine.setAnswer('shoe-shank', 'no');
            } else {
                // "No" - clear implicit shank so user picks manually
                delete this.decisionEngine.answers['shoe-shank'];
                delete this.decisionEngine.answers['arch-height'];
            }
        }

        // Update navigation buttons
        this.updateNavigationButtons();
    }

    renderProgressDots() {
        this.progressDots.innerHTML = '';
        
        for (let i = 0; i < this.totalSteps; i++) {
            // Skip dots for screens that are being skipped
            if (this.shouldSkipScreen(i)) {
                continue;
            }
            
            const dot = document.createElement('div');
            dot.className = 'assmt-progress-dots__dot';
            
            if (i < this.currentStep) {
                dot.classList.add('assmt-progress-dots__dot--completed');
            } else if (i === this.currentStep) {
                dot.classList.add('assmt-progress-dots__dot--active');
            }
            
            this.progressDots.appendChild(dot);
        }
    }

    // Check if all questions on current screen have been answered
    isCurrentScreenComplete() {
        const screen = this.screens[this.currentStep];
        return screen.questions.every(question => {
            // Skip conditional questions whose condition isn't met
            if (question.conditionalOn) {
                const parentAnswer = this.decisionEngine.answers[question.conditionalOn.questionId];
                if (parentAnswer !== question.conditionalOn.value) {
                    return true; // Not required, skip
                }
            }
            return this.decisionEngine.answers[question.id] !== undefined;
        });
    }

    updateNavigationButtons() {
        // Hide prev button on first question
        if (this.currentStep === 0) {
            this.prevBtn.style.opacity = '0';
            this.prevBtn.style.pointerEvents = 'none';
        } else {
            this.prevBtn.style.opacity = '1';
            this.prevBtn.style.pointerEvents = 'auto';
            this.prevBtn.disabled = false;
        }

        // Update next button - enabled only if all questions on screen are answered
        this.nextBtn.disabled = !this.isCurrentScreenComplete();

        // Always show "Next"
        this.nextBtn.textContent = 'Next';
    }

    nextQuestion() {
        if (this.isTransitioning) return;
        
        // Check if we're on results screen
        const btnText = this.nextBtn.textContent;
        if (btnText === 'Restart Assessment' || btnText === 'Restart') {
            this.restart();
            return;
        }
        
        // Hide hero when leaving first screen
        if (this.currentStep === 0) {
            this.toggleHero(false);
        } else {
            if (window.innerWidth <= 768) {
                setTimeout(() => {
                    const container = document.querySelector('.assmt__question-wrapper');
                    if (container) {
                        const rect = container.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const targetY = rect.top + scrollTop - 80;
                        window.scrollTo({ top: targetY, behavior: 'smooth' });
                    }
                }, 100);
            }
        }
        
        // Check if Poor Mobility was selected on mobility screen — skip to limb length
        if (this.currentStep === this.screenIndices.mobility && this.decisionEngine.answers['foot-mobility'] === 'poor') {
            this.transitionToStep(this.screenIndices.limbLength, 'next');
            return;
        }

        // Get next valid screen
        const nextStep = this.getNextScreenIndex(this.currentStep);
        
        if (nextStep < this.totalSteps) {
            this.transitionToStep(nextStep, 'next');
        } else {
            this.showResults();
        }
    }

    previousQuestion() {
        if (this.isTransitioning) return;
        
        // Check if we're on results screen
        if (this.resultsContainer.style.display === 'block') {
            this.backToAssessment();
            return;
        }
        
        if (this.currentStep > 0) {
            const prevStep = this.getPrevScreenIndex(this.currentStep);
            if (prevStep >= 0) {
                this.transitionToStep(prevStep, 'prev');
            }
        }
    }

    getVisibleQuestionNumber(targetQuestionId) {
        let count = 0;
        
        for (let i = 0; i < this.screens.length; i++) {
            if (this.shouldSkipScreen(i)) continue;
            
            for (const q of this.screens[i].questions) {
                if (q.type === 'size-selector') continue;
                
                // Skip conditional questions whose condition isn't met
                if (q.conditionalOn) {
                    const parentAnswer = this.decisionEngine.answers[q.conditionalOn.questionId];
                    if (parentAnswer !== q.conditionalOn.value) continue;
                }
                
                count++;
                if (q.id === targetQuestionId) return count;
            }
        }
        return null;
    }

    transitionToStep(newStep, direction) {
        this.isTransitioning = true;
        
        // Show hero when returning to first screen
        const showingHero = newStep === 0 && this.currentStep > 0;
        if (showingHero) {
            this.toggleHero(true);
        }
        
        // Scroll on mobile
        if (window.innerWidth <= 768) {
            setTimeout(() => {
                if (showingHero) {
                    const header = document.querySelector('.assmt__header');
                    if (header) {
                        const rect = header.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const targetY = rect.top + scrollTop - 20;
                        window.scrollTo({ top: targetY, behavior: 'smooth' });
                    }
                } else {
                    const container = document.querySelector('.assmt__question-wrapper');
                    if (container) {
                        const rect = container.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const targetY = rect.top + scrollTop - 80;
                        window.scrollTo({ top: targetY, behavior: 'smooth' });
                    }
                }
            }, showingHero ? 600 : 100);
        }
        
        // Get current height of main container
        const currentHeight = this.questionContainerMain.offsetHeight;
        
        // Set explicit height on wrapper to current height
        this.questionWrapper.style.height = currentHeight + 'px';
        
        // Render new screen content in overlay
        const screen = this.screens[newStep];
        this.questionContainerOverlayContent.innerHTML = this.generateScreenHTML(screen);
        
        // Show overlay
        this.questionContainerOverlay.style.display = 'block';
        
        // Get new height after content is rendered
        const newHeight = this.questionContainerOverlay.offsetHeight;
        
        // Animate wrapper to new height
        requestAnimationFrame(() => {
            this.questionWrapper.style.height = newHeight + 'px';
        });
        
        // Reset animation classes
        this.questionContainerMain.className = 'assmt__question-container';
        this.questionContainerOverlay.className = 'assmt__question-container assmt__question-container--overlay';
        
        // Trigger reflow
        void this.questionContainerMain.offsetWidth;
        
        // Start animations based on direction
        if (direction === 'next') {
            this.questionContainerMain.classList.add('assmt__question-container--slide-out-left');
            this.questionContainerOverlay.classList.add('assmt__question-container--slide-in-right');
        } else {
            this.questionContainerMain.classList.add('assmt__question-container--slide-out-right');
            this.questionContainerOverlay.classList.add('assmt__question-container--slide-in-left');
        }
        
        // After animation completes
        setTimeout(() => {
            this.currentStep = newStep;
            this.renderScreen();
            this.renderProgressDots();
            
            this.questionContainerOverlay.style.display = 'none';
            
            this.questionContainerMain.className = 'assmt__question-container';
            this.questionContainerOverlay.className = 'assmt__question-container assmt__question-container--overlay';
            
            this.questionWrapper.style.height = 'auto';
            
            this.isTransitioning = false;
        }, 500);
    }

    openVideoModal(question) {     
        const isYouTube = question.videoLink.includes('youtube.com') || question.videoLink.includes('youtu.be');
        
        if (isYouTube) {
            let videoId = '';
            
            if (question.videoLink.includes('youtube.com/watch?v=')) {
                videoId = question.videoLink.split('v=')[1]?.split('&')[0]?.split('#')[0];
            } else if (question.videoLink.includes('youtu.be/')) {
                videoId = question.videoLink.split('youtu.be/')[1]?.split('?')[0]?.split('#')[0];
            } else if (question.videoLink.includes('youtube.com/embed/')) {
                videoId = question.videoLink.split('embed/')[1]?.split('?')[0]?.split('#')[0];
            }
            
            if (videoId) {
                this.modalVideo.style.display = 'none';
                this.modalIframe.style.display = 'block';
                this.modalIframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`;
            } else {
                alert('Invalid YouTube URL');
                return;
            }
        } else {
            this.modalIframe.style.display = 'none';
            this.modalVideo.style.display = 'block';
            
            const videoSource = this.modalVideo.querySelector('source');
            videoSource.src = question.videoLink;
            this.modalVideo.load();
        }
        
        this.videoModal.classList.add('assmt-modal--visible');
        document.body.style.overflow = 'hidden';
    }

    closeVideoModal() {
        this.videoModal.classList.remove('assmt-modal--visible');
        this.modalVideo.pause();
        this.modalIframe.src = '';
        document.body.style.overflow = '';
    }

    showResults() {
        if (this.isTransitioning) return;
        this.isTransitioning = true;
        
        // Hide hero on results screen if it's visible
        if (this.heroSection) {
            const computedStyle = window.getComputedStyle(this.heroSection);
            if (computedStyle.display !== 'none') {
                this.toggleHero(false);
            }
        }
        
        // Scroll to top of results on mobile
        if (window.innerWidth <= 768) {
            setTimeout(() => {
                const container = document.querySelector('.assmt__question-wrapper');
                if (container) {
                    const rect = container.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop - 80;
                    window.scrollTo({ top: targetY, behavior: 'smooth' });
                }
            }, 600);
        }
        
        const recommendation = this.decisionEngine.getRecommendation();

        // Get product URL based on productKey and size
        const productUrls = this.productData.urls[recommendation.productKey] || {};
        const productUrl = productUrls[recommendation.size] || Object.values(productUrls)[0] || '#';
        
        // Get product image based on imageKey
        const productImage = this.productData.images[recommendation.imageKey] || this.productData.images['bio_advance_full_length_medium_none'] || '';

        const productContainer = document.getElementById('assmt-results-product');
        
        let productTitle = recommendation.device;
        let productSubtitle = recommendation.density ? `(${recommendation.density} Density)` : '';
        let productDescription = recommendation.narrative;

        const sizeRegionLabel = this.decisionEngine.answers['size-region-label'] || 'UK';
        const sizeLabel = this.decisionEngine.answers['size-label'] || 'M';
        const sizeDisplay = `${sizeRegionLabel} ${sizeLabel}`;

        // Build components HTML (shared by both cards)
        const componentsHTML = recommendation.components && recommendation.components.length > 0 ? 
            recommendation.components.map(c => {
                // Extract the label (e.g. "Limb Length Discrepancy" or "Heel Pain") and value
                const isHeelLift = c.toLowerCase().includes('heel lift');
                const label = isHeelLift ? 'Limb Length Discrepancy' : 'Heel Pain';
                // Make the suggestion text green and bold, with the parenthetical smaller
                const mainText = c.split('(')[0].trim();
                const subText = c.includes('(') ? '(' + c.split('(').slice(1).join('(') : '';
                return `
                    <div class="assmt-results__section assmt-results__section--full">
                        <p class="assmt-results__section-title">${label}</p>
                        <p class="assmt-results__section-value">${mainText}</p>
                        ${subText ? `<p class="assmt-results__section-sub">${subText}</p>` : ''}
                    </div>
                `;
            }).join('')
        : '';

        const now = new Date();
        const printDateTime = now.toLocaleString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        }).replace(',', '');

        const printHeaderHTML = `
            <div class="assmt-print-header">
                ${this.logoSrc ? `<img src="${this.logoSrc}" alt="Logo" class="assmt-print-header__logo">` : ''}
                <h2 class="assmt-print-header__title">CLINICAL PATIENT ASSESSMENT</h2>
                <div class="assmt-print-header__fields">
                    <div class="assmt-print-header__field">
                        <span class="assmt-print-header__label">Practitioner Name:</span>
                        <hr class="assmt-print-header__line">
                    </div>
                    <div class="assmt-print-header__field">
                        <span class="assmt-print-header__label">Patient Name:</span>
                        <hr class="assmt-print-header__line">
                    </div>
                    <div class="assmt-print-header__field">
                        <span class="assmt-print-header__label">Date/Time of Assessment:</span>
                        <div class="assmt-print-header__line">${printDateTime}</div>
                    </div>
                </div>
            </div>
        `;

        const printNotesHTML = `
            <div class="assmt-print-notes">
                <p class="assmt-print-notes__label">Practitioner Notes:</p>
                <hr class="assmt-print-notes__line">
                <hr class="assmt-print-notes__line">
                <hr class="assmt-print-notes__line">
            </div>
        `;

        // Build primary card
        let cardsHTML = printHeaderHTML + `
            <div class="assmt-results__card assmt-results__card--primary">
                <p class="assmt-results__card-label">(1) We Recommend</p>
                <div class="assmt-results__card-product">
                    <div class="assmt-results__card-info">
                        <h3 class="assmt-results__product-title">${productTitle}</h3>
                        <p class="assmt-results__product-description">${productDescription}</p>
                        <div class="assmt-results__sections">
                            ${recommendation.density ? `
                                <div class="assmt-results__section">
                                    <p class="assmt-results__section-title">Density</p>
                                    <p class="assmt-results__section-value">${recommendation.density}</p>
                                </div>
                            ` : ''}
                            <div class="assmt-results__section assmt-results__section--size">
                                <p class="assmt-results__section-title">Size</p>
                                <p class="assmt-results__section-value">${sizeDisplay}</p>
                            </div>
                            ${recommendation.postingDisplay ? `
                               <div class="assmt-results__section assmt-results__section--full">
                                    <p class="assmt-results__section-title">Posting</p>
                                    <p class="assmt-results__section-value">${recommendation.postingDisplay}</p>
                                </div>
                            ` : ''}
                            ${componentsHTML}
                        </div>
                        <div class="assmt-results__card-print-link">
                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(productUrl)}" alt="QR Code">
                            <div class="assmt-results__card-print-link-text">
                                <span class="assmt-results__card-print-link-label">View Product Online</span>
                                <span class="assmt-results__card-print-link-url">${productUrl}</span>
                            </div>
                        </div>
                        <div class="assmt-results__card-actions">
                            <button class="assmt-btn assmt-btn--primary" data-print-outcome="1">Print Outcome (1)</button>
                            <button class="assmt-btn assmt-btn--primary" onclick="window.open('${productUrl}', '_blank')">View Product Online</button>
                        </div>
                    </div>
                    <div class="assmt-results__card-image">
                        <img src="${productImage}" alt="${productTitle}" class="assmt-results__product-img">
                    </div>
                </div>
            </div>
        `;

        // Build alternative card if present
        if (recommendation.alternative) {
            const alt = recommendation.alternative;
            const altProductUrls = this.productData.urls[alt.productKey] || {};
            const altProductUrl = altProductUrls[recommendation.size] || Object.values(altProductUrls)[0] || '#';
            const altProductImage = this.productData.images[alt.imageKey] || '';

            cardsHTML += `
                <div class="assmt-results__card assmt-results__card--alt">
                    <p class="assmt-results__card-label">(2) We Alternatively Recommend</p>
                    <div class="assmt-results__card-product">
                        <div class="assmt-results__card-info">
                            <h3 class="assmt-results__product-title">${alt.device}</h3>
                            <p class="assmt-results__product-description">${alt.narrative}</p>
                            <div class="assmt-results__sections">
                                <div class="assmt-results__section">
                                    <p class="assmt-results__section-title">Size</p>
                                    <p class="assmt-results__section-value">${sizeDisplay}</p>
                                </div>
                                ${alt.postingDisplay ? `
                                    <div class="assmt-results__section assmt-results__section--full">
                                        <p class="assmt-results__section-title">Posting</p>
                                        <p class="assmt-results__section-value">${alt.postingDisplay}</p>
                                    </div>
                                ` : ''}
                                ${componentsHTML}
                            </div>
                            <div class="assmt-results__card-print-link">
                                <img src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(altProductUrl)}" alt="QR Code">
                                <div class="assmt-results__card-print-link-text">
                                    <span class="assmt-results__card-print-link-label">View Product Online</span>
                                    <span class="assmt-results__card-print-link-url">${altProductUrl}</span>
                                </div>
                            </div>
                            <div class="assmt-results__card-actions">
                                <button class="assmt-btn assmt-btn--primary" data-print-outcome="2">Print Outcome (2)</button>
                                <button class="assmt-btn assmt-btn--primary" onclick="window.open('${altProductUrl}', '_blank')">View Product Online</button>
                            </div>
                        </div>
                        <div class="assmt-results__card-image">
                            <img src="${altProductImage}" alt="${alt.device}" class="assmt-results__product-img">
                        </div>
                    </div>
                </div>
            `;
        }

        cardsHTML += printNotesHTML;

        productContainer.innerHTML = cardsHTML;

        const printOutcomeBtns = productContainer.querySelectorAll('[data-print-outcome]');
        printOutcomeBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                this.printOutcome(btn.dataset.printOutcome);
            });
        });

        // Get current height
        const currentHeight = this.questionContainerMain.offsetHeight;
        this.questionWrapper.style.height = currentHeight + 'px';

        // Show results container and prepare it
        this.resultsContainer.style.display = 'block';
        this.resultsContainer.classList.remove('assmt__question-container--overlay');
        this.resultsContainer.style.position = 'absolute';
        this.resultsContainer.style.top = '0';
        this.resultsContainer.style.left = '0';
        this.resultsContainer.style.right = '0';
        this.resultsContainer.style.zIndex = '1';
        
        // Get new height
        const newHeight = this.resultsContainer.offsetHeight;
        
        // Animate wrapper height
        requestAnimationFrame(() => {
            this.questionWrapper.style.height = newHeight + 'px';
        });

        // Reset animation classes
        this.questionContainerMain.className = 'assmt__question-container';
        this.resultsContainer.className = 'assmt__question-container';
        
        // Trigger reflow
        void this.questionContainerMain.offsetWidth;
        
        // Start animations
        this.questionContainerMain.classList.add('assmt__question-container--slide-out-left');
        this.resultsContainer.classList.add('assmt__question-container--slide-in-right');
        
        setTimeout(() => {
            this.questionContainerMain.style.display = 'none';
            
            this.resultsContainer.style.position = 'relative';
            this.resultsContainer.style.zIndex = 'auto';
            this.resultsContainer.style.top = '';
            this.resultsContainer.style.left = '';
            this.resultsContainer.style.right = '';
            
            // Update navigation buttons for results screen
            this.prevBtn.style.opacity = '1';
            this.prevBtn.style.pointerEvents = 'auto';
            this.prevBtn.textContent = 'Prev';
            this.prevBtn.disabled = false;
            
            if (window.innerWidth <= 768) {
                this.nextBtn.textContent = 'Restart';
            } else {
                this.nextBtn.textContent = 'Restart Assessment';
            }
            this.nextBtn.disabled = false;
            
            this.questionContainerMain.className = 'assmt__question-container';
            this.resultsContainer.className = 'assmt__question-container';
            this.questionWrapper.style.height = 'auto';
            this.isTransitioning = false;
        }, 500);
    }

    backToAssessment() {
        if (this.isTransitioning) return;
        this.isTransitioning = true;
        
        // Always go back to the last screen (heel-pain)
        this.currentStep = this.totalSteps - 1;
        
        // Show hero if going back to first screen
        const showingHero = this.currentStep === 0;
        if (showingHero) {
            this.toggleHero(true);
        }
        
        // Scroll on mobile
        if (window.innerWidth <= 768) {
            setTimeout(() => {
                if (showingHero) {
                    const header = document.querySelector('.assmt__header');
                    if (header) {
                        const rect = header.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const targetY = rect.top + scrollTop - 20;
                        window.scrollTo({ top: targetY, behavior: 'smooth' });
                    }
                } else {
                    const container = document.querySelector('.assmt__question-wrapper');
                    if (container) {
                        const rect = container.getBoundingClientRect();
                        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                        const targetY = rect.top + scrollTop - 80;
                        window.scrollTo({ top: targetY, behavior: 'smooth' });
                    }
                }
            }, 600);
        }
        
        // Render content in overlay
        const screen = this.screens[this.currentStep];
        this.questionContainerOverlayContent.innerHTML = this.generateScreenHTML(screen);
        
        // Get heights
        const currentHeight = this.resultsContainer.offsetHeight;
        this.questionWrapper.style.height = currentHeight + 'px';
        
        // Show overlay
        this.questionContainerOverlay.style.display = 'block';
        const newHeight = this.questionContainerOverlay.offsetHeight;
        
        // Animate height
        requestAnimationFrame(() => {
            this.questionWrapper.style.height = newHeight + 'px';
        });
        
        // Reset animation classes
        this.resultsContainer.className = 'assmt__question-container';
        this.questionContainerOverlay.className = 'assmt__question-container assmt__question-container--overlay';
        
        // Trigger reflow
        void this.resultsContainer.offsetWidth;
        
        // Slide out results, slide in question
        this.resultsContainer.classList.add('assmt__question-container--slide-out-right');
        this.questionContainerOverlay.classList.add('assmt__question-container--slide-in-left');
        
        setTimeout(() => {
            this.resultsContainer.style.display = 'none';
            this.questionContainerOverlay.style.display = 'none';
            
            this.questionContainerMain.style.display = 'block';
            this.renderScreen();
            this.renderProgressDots();
            this.updateNavigationButtons();
            
            this.resultsContainer.className = 'assmt__question-container assmt__question-container--overlay';
            this.questionContainerOverlay.className = 'assmt__question-container assmt__question-container--overlay';
            this.questionContainerMain.className = 'assmt__question-container';
            this.questionWrapper.style.height = 'auto';
            this.isTransitioning = false;
        }, 500);
    }

    restart() {
        if (this.isTransitioning) return;
        this.isTransitioning = true;
        
        // Show hero when restarting
        this.toggleHero(true);
        
        // Scroll to show logo/header on mobile when restarting
        if (window.innerWidth <= 768) {
            setTimeout(() => {
                const header = document.querySelector('.assmt__header');
                if (header) {
                    const rect = header.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop - 20;
                    window.scrollTo({ top: targetY, behavior: 'smooth' });
                }
            }, 600);
        }
        
        // Reset decision engine
        this.decisionEngine.answers = {};
        this.currentStep = 0;
        
        // Check if results are showing
        const isResultsVisible = this.resultsContainer.style.display === 'block';
        
        if (isResultsVisible) {
            // Render first screen in overlay
            const screen = this.screens[0];
            this.questionContainerOverlayContent.innerHTML = this.generateScreenHTML(screen);
            
            // Get heights
            const currentHeight = this.resultsContainer.offsetHeight;
            this.questionWrapper.style.height = currentHeight + 'px';
            
            // Show overlay
            this.questionContainerOverlay.style.display = 'block';
            const newHeight = this.questionContainerOverlay.offsetHeight;
            
            // Animate height
            requestAnimationFrame(() => {
                this.questionWrapper.style.height = newHeight + 'px';
            });
            
            // Reset animation classes
            this.resultsContainer.className = 'assmt__question-container';
            this.questionContainerOverlay.className = 'assmt__question-container assmt__question-container--overlay';
            
            // Trigger reflow
            void this.resultsContainer.offsetWidth;
            
            // Slide animations
            this.resultsContainer.classList.add('assmt__question-container--slide-out-right');
            this.questionContainerOverlay.classList.add('assmt__question-container--slide-in-left');
            
            setTimeout(() => {
                this.resultsContainer.style.display = 'none';
                this.questionContainerOverlay.style.display = 'none';
                
                this.questionContainerMain.style.display = 'block';
                this.renderScreen();
                this.renderProgressDots();
                this.updateNavigationButtons();
                
                this.resultsContainer.className = 'assmt__question-container assmt__question-container--overlay';
                this.questionContainerOverlay.className = 'assmt__question-container assmt__question-container--overlay';
                this.questionContainerMain.className = 'assmt__question-container';
                this.questionWrapper.style.height = 'auto';
                this.isTransitioning = false;
            }, 500);
        } else {
            this.renderScreen();
            this.renderProgressDots();
            this.updateNavigationButtons();
            this.isTransitioning = false;
        }
    }
}